import React, { useEffect, useRef, useState } from "react";
import {
  FormControl,
  InputAdornment,
  Stack,
  TextareaAutosize,
  Typography,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircularProgress from "@mui/material/CircularProgress";

import CheckBox from "../../../../styles/svg/id-icon.svg";
import { useResponsive } from "../../../../hooks/useResponsive";
import { CustomButton } from "../../../../components/Button/Button";

function FieldCreateEditCard({
  handleKeyDown,
  setTitle,
  setCreateBtnOpen,
  edit = false,
  value,
  minRows = 3,
  className = "create-card-textarea",
  ref = null,
  setOpenCreateSubtask,
  preventCloseOnTabSwitch = false,
  loading = false,
  disabled = false,
}) {
  const [error, setError] = useState(false);
  const isMobile = useResponsive("down", "md");
  const textareaRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  const handleChange = (event) => {
    const value = event.target.value.trimStart();
    setTitle(value);
    if (value.length <= 255) {
      setError(false);
    } else {
      setError(true);
    }
  };

  useEffect(() => {
      if (textareaRef.current) {
        requestAnimationFrame(() => {
          const length = textareaRef.current.value.length;
          textareaRef.current.setSelectionRange(length, length); // Move cursor to end
          textareaRef.current.focus(); // Ensure focus
        });
      }
      
      // Cleanup timeout on unmount
      return () => {
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
        }
      };
    }, []);

  return (
    <FormControl fullWidth onClick={(e) => e.stopPropagation()} ref={ref} sx={{ position: "relative" }}>
      <TextareaAutosize
        aria-label="empty textarea"
        placeholder="What needs to be done?"
        minRows={minRows} // Set the minimum number of rows
        className={`${className} ${error ? "create-card-error-textarea" : ""}`}
        // defaultValue={value}
        value={value}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onBlur={
          isMobile || loading
            ? () => {
                "";
              }
            : (e) => {
                if (preventCloseOnTabSwitch) {
                  // Clear any existing timeout
                  if (blurTimeoutRef.current) {
                    clearTimeout(blurTimeoutRef.current);
                  }
                  
                  // Use a delay to check if tab is switching
                  blurTimeoutRef.current = setTimeout(() => {
                    // Don't close if:
                    // 1. Document is hidden (tab is switching)
                    // 2. User has typed something (value exists)
                    // Only close if document is visible AND value is empty (user clicked away intentionally)
                    if (!document.hidden && !value?.trim()) {
                      setCreateBtnOpen(false);
                    }
                    blurTimeoutRef.current = null;
                  }, 150);
                } else {
                  setCreateBtnOpen(false);
                }
              }
        }
        autoFocus
        ref={textareaRef}
        disabled={disabled}
        style={{ opacity: disabled ? 0.7 : 1 }}
      />
      
      {/* Small loader in bottom right corner */}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            bottom: "8px",
            right: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={16} sx={{ color: "rgb(37, 150, 190)" }} />
        </Box>
      )}

      {!edit && (
        <InputAdornment position="end">
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Stack direction={"row"}>
              <img src={CheckBox} alt="checkbox" className=" mb-6" />
              <ExpandMoreIcon
                className="mb-6"
                onClick={() => setOpenCreateSubtask(false)}
              />
            </Stack>
            {isMobile && (
              <CustomButton
                className="mb-6 mr-2 edit-card-submit-btn"
                sx={{
                  color: "white",
                  backgroundColor: "rgb(37, 150, 190)",
                  padding: "0px",
                  fontSize: "12px",
                  opacity: loading ? 0.6 : 1,
                }}
                onClick={(event) => {
                  if (!loading) {
                    handleKeyDown({ ...event, key: "btnClicked" });
                  }
                }}
                disabled={loading}
              >
                Done
              </CustomButton>
            )}
          </Stack>
        </InputAdornment>
      )}
      {error && (
        <Typography
          variant="body2"
          color="error"
          className="card-error-font-size"
        >
          Character limit exceeded! Maximum 255 characters allowed.
        </Typography>
      )}
    </FormControl>
  );
}

export default FieldCreateEditCard;
